import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, Users, Hash, ArrowLeft } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';

const API_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http:\/\//i, 'https://') + '/api';
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http/i, 'ws').replace(/^wss:\/\//i, 'wss://') + '/ws';

const ChatPage = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [online, setOnline] = useState(0);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (activeRoom) connectWS(activeRoom);
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [activeRoom]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchRooms = async () => {
    try {
      const res = await fetch(`${API_URL}/chat/rooms`);
      if (res.ok) {
        const data = await res.json();
        setRooms(data.rooms || []);
        setOnline(data.online || 0);
      }
    } catch (e) { console.error(e); }
  };

  const connectWS = (roomId) => {
    if (wsRef.current) wsRef.current.close();
    // Fallback to HTTP polling if WS not available
    fetchHistory(roomId);
    try {
      const ws = new WebSocket(`${WS_URL}/chat/${roomId}`);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === 'history') setMessages(data.messages || []);
        else if (data.type === 'message') setMessages(prev => [...prev, data.data]);
      };
      ws.onerror = () => fetchHistory(roomId);
      wsRef.current = ws;
    } catch { fetchHistory(roomId); }
  };

  const fetchHistory = async (roomId) => {
    try {
      const res = await fetch(`${API_URL}/chat/history/${roomId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { console.error(e); }
  };

  const sendMessage = () => {
    if (!input.trim() || !activeRoom) return;
    const msg = { user_id: user.user_id || 'anon', username: user.username || 'Anonym', text: input };
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      // HTTP fallback
      setMessages(prev => [...prev, { ...msg, timestamp: new Date().toISOString(), type: 'message' }]);
    }
    setInput('');
  };

  if (!activeRoom) {
    return (
      <div className="min-h-screen bg-[#121212] pb-24">
        <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <MessageCircle className="w-6 h-6 text-[#00BFA6]" />
            <h1 className="text-xl font-bold text-white">Live Chat</h1>
            <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
              <Users className="w-3 h-3" /> {online} online
            </span>
          </div>
        </header>
        <div data-testid="chat-rooms-page" className="max-w-2xl mx-auto px-4 py-6">
          <div className="space-y-2">
            {rooms.map(room => (
              <button
                key={room.id}
                data-testid={`chat-room-${room.id}`}
                onClick={() => setActiveRoom(room.id)}
                className="w-full p-4 bg-[#1E1E1E] border border-white/5 rounded-xl hover:border-[#00BFA6]/30 transition-all flex items-center gap-3 text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00BFA6]/10 flex items-center justify-center">
                  <Hash className="w-5 h-5 text-[#00BFA6]" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{room.name}</div>
                  <div className="text-xs text-gray-500">{room.description}</div>
                </div>
                <div className="text-xs text-gray-600">{room.message_count} Nachrichten</div>
              </button>
            ))}
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] flex flex-col">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button onClick={() => setActiveRoom(null)} className="p-1.5 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <Hash className="w-4 h-4 text-[#00BFA6]" />
          <span className="text-sm font-medium text-white">{activeRoom}</span>
        </div>
      </header>
      <div data-testid="chat-active-room" className="max-w-2xl mx-auto w-full px-4 py-4 flex flex-col flex-1" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        <div className="flex-1 overflow-y-auto space-y-2 mb-3 pr-1">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.user_id === user.user_id ? 'justify-end' : ''}`}>
              <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${
                msg.user_id === user.user_id
                  ? 'bg-[#00BFA6]/20 text-[#00BFA6]'
                  : 'bg-[#1E1E1E] text-white'
              }`}>
                <div className="text-[10px] text-gray-500 mb-0.5">{msg.username}</div>
                <div>{msg.text}</div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <div className="flex gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Nachricht schreiben..."
            className="flex-1 px-4 py-2.5 bg-[#1E1E1E] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:border-[#00BFA6]/50 focus:outline-none"
          />
          <button
            data-testid="chat-send-btn"
            onClick={sendMessage}
            className="px-4 py-2.5 bg-[#00BFA6] text-black rounded-xl font-medium text-sm hover:bg-[#00BFA6]/90 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
